<template>
  <div class="dist-list-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Distribuidores / Listado</p>
            <h1 class="list-title">Listado de Distribuidores</h1>
            <p class="list-subtitle">
              Consulte y administre todos los distribuidores del sistema.
            </p>
          </div>
        </v-flex>

        <v-flex xs12>
          <v-card class="dist-card">
            <v-card-text class="dist-card__toolbar">
              <v-layout row wrap align-center>
                <v-flex md11 sm10 xs12>
                  <v-text-field
                    v-model="search"
                    class="list-search"
                    append-icon="search"
                    clearable
                    hide-details
                    placeholder="Buscar por nombre, teléfono o contacto..."
                    solo
                    @keyup.enter="onSearch"
                    @click:append="onSearch"
                  ></v-text-field>
                </v-flex>
                <v-flex md1 sm2 xs12 class="toolbar-actions">
                  <v-btn
                    icon
                    class="btn-download"
                    title="Exportar Excel"
                    :loading="exporting"
                    :disabled="exporting"
                    @click="onDownload"
                  >
                    <v-icon small>cloud_download</v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>

            <!-- Desktop -->
            <v-card-text class="dist-card__content hidden-sm-and-down">
              <div class="table-wrap">
                <table class="dist-table">
                  <thead>
                    <tr>
                      <th>Distribuidor</th>
                      <th>Teléfono</th>
                      <th>Dirección</th>
                      <th>Contacto</th>
                      <th>Estado</th>
                      <th class="th-actions">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="dist in sortedDistributors" :key="dist.id">
                      <td>{{ dist.name }}</td>
                      <td>{{ dist.phone }}</td>
                      <td>
                        <span v-if="dist.address">{{ dist.address }}</span>
                        <span v-else class="empty-cell">—</span>
                      </td>
                      <td>
                        <span v-if="hasNotifications(dist) && dist.contact">
                          {{ dist.contact.firstName }}
                          {{ dist.contact.lastName }}
                        </span>
                        <span v-else class="empty-cell">—</span>
                      </td>
                      <td>
                        <span
                          :class="[
                            'status-chip',
                            isActive(dist)
                              ? 'status-chip--active'
                              : 'status-chip--inactive',
                          ]"
                        >
                          {{ isActive(dist) ? 'Activo' : 'Inactivo' }}
                        </span>
                      </td>
                      <td>
                        <div class="action-group">
                          <v-btn
                            class="btn-edit"
                            icon
                            small
                            :to="`/distribuidores/editar/${dist.id}`"
                            title="Editar"
                          >
                            <v-icon small>edit</v-icon>
                          </v-btn>
                          <v-btn
                            class="btn-delete"
                            icon
                            small
                            @click="openDeleteModal(dist.id, dist.name)"
                            title="Eliminar"
                          >
                            <v-icon small>delete_outline</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!pagination.loading && distributors.length === 0">
                      <td colspan="6" class="empty-state">
                        No se encontraron distribuidores con los filtros
                        aplicados.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>

            <!-- Mobile -->
            <v-card-text class="dist-card__content-mobile hidden-md-and-up">
              <div
                v-if="!pagination.loading && distributors.length === 0"
                class="mobile-empty"
              >
                No se encontraron distribuidores con los filtros aplicados.
              </div>
              <v-layout row wrap>
                <v-flex
                  xs12
                  v-for="dist in sortedDistributors"
                  :key="`card-${dist.id}`"
                >
                  <v-card class="dist-mobile-card">
                    <v-card-text>
                      <div class="mobile-row">
                        <span class="mobile-label">Nombre</span>
                        <span class="mobile-value">{{ dist.name }}</span>
                      </div>
                      <div class="mobile-row">
                        <span class="mobile-label">Teléfono</span>
                        <span class="mobile-value">{{ dist.phone }}</span>
                      </div>
                      <div class="mobile-row" v-if="dist.address">
                        <span class="mobile-label">Dirección</span>
                        <span class="mobile-value">{{ dist.address }}</span>
                      </div>
                      <div
                        class="mobile-row"
                        v-if="hasNotifications(dist) && dist.contact"
                      >
                        <span class="mobile-label">Contacto</span>
                        <span class="mobile-value">
                          {{ dist.contact.firstName }}
                          {{ dist.contact.lastName }}
                        </span>
                      </div>
                      <div class="mobile-row">
                        <span class="mobile-label">Estado</span>
                        <span class="mobile-value">
                          <span
                            :class="[
                              'status-chip',
                              isActive(dist)
                                ? 'status-chip--active'
                                : 'status-chip--inactive',
                            ]"
                          >
                            {{ isActive(dist) ? 'Activo' : 'Inactivo' }}
                          </span>
                        </span>
                      </div>
                      <div class="mobile-actions">
                        <v-btn
                          class="btn-edit"
                          icon
                          small
                          :to="`/distribuidores/editar/${dist.id}`"
                          title="Editar"
                        >
                          <v-icon small>edit</v-icon>
                        </v-btn>
                        <v-btn
                          class="btn-delete"
                          icon
                          small
                          @click="openDeleteModal(dist.id, dist.name)"
                          title="Eliminar"
                        >
                          <v-icon small>delete_outline</v-icon>
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex xs12 v-if="pagination.pages">
          <div class="list-footer">
            <div class="list-footer__count">
              Mostrando {{ distributors.length }} de
              {{ pagination.totalItems }} distribuidores
            </div>
            <v-pagination
              v-model="pagination.page"
              :length="pagination.pages"
              :total-visible="7"
              @input="asyncPagination"
            ></v-pagination>
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog max-width="350" persistent v-model="loading">
      <loading></loading>
    </v-dialog>

    <dialog-confirm
      v-model="distDelete.modal"
      title="Eliminar Distribuidor"
      message="Esta acción eliminará el distribuidor del sistema de forma permanente."
      :item-name="distDelete.name"
      :loading="distDelete.loading"
      @confirm="deleteDist"
    ></dialog-confirm>

    <v-snackbar
      v-model="snack.show"
      :timeout="4000"
      :color="snack.color"
      top
      right
    >
      {{ snack.message }}
      <v-btn flat dark @click="snack.show = false">Cerrar</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import loading from './../dialog/DialogLoading'
import DialogConfirm from './../dialog/DialogConfirm'

export default {
  components: { loading, DialogConfirm },
  data() {
    return {
      search: '',
      loading: true,
      exporting: false,
      distDelete: { modal: false, id: null, name: '', loading: false },
      snack: { show: false, color: 'info', message: '' },
      pagination: {
        page: 1,
        rpp: this.$config.resultsPerPage,
        totalItems: 0,
        loading: true,
        pages: 0,
        search: {},
      },
    }
  },
  created() {
    this.asyncPagination()
  },
  computed: {
    ...mapState({
      distributors: state => state.distributors.all,
    }),
    sortedDistributors() {
      return [...this.distributors].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'es', {
          sensitivity: 'base',
        })
      )
    },
  },
  methods: {
    helperPaginate(response) {
      this.pagination.totalItems = response.total
      this.pagination.pages =
        Math.ceil(this.pagination.totalItems / this.pagination.rpp) || 0
      this.loading = false
      this.pagination.loading = false
    },
    asyncPagination() {
      this.pagination.loading = true
      const filter = {
        rpp: this.pagination.rpp,
        page: this.pagination.page,
        ...this.pagination.search,
      }
      return new Promise(resolve => {
        this.$store.dispatch('distributors/getAll', filter).then(response => {
          this.helperPaginate(response)
          return resolve()
        })
      })
    },
    onSearch() {
      const params = { term: this.search }
      Object.keys(params).forEach(key => {
        if (params[key] == null || params[key] === '') delete params[key]
      })
      this.pagination.search = params
      this.pagination.page = 1
      this.asyncPagination()
    },
    hasNotifications(dist) {
      if (!dist) return false
      if (typeof dist.notificationsEnabled === 'boolean')
        return dist.notificationsEnabled
      if (Array.isArray(dist.emails)) return dist.emails.length > 0
      return !!(dist.emails && String(dist.emails).trim().length)
    },
    isActive(dist) {
      if (!dist) return false
      const raw =
        dist.active != null
          ? dist.active
          : dist.status != null
          ? dist.status
          : dist.enabled
      if (raw == null) return true
      if (typeof raw === 'string') {
        const v = raw.toLowerCase()
        return v === 'activo' || v === 'active' || v === '1' || v === 'true'
      }
      return !!raw
    },
    openDeleteModal(id, name) {
      this.distDelete = { modal: true, id, name: name || '', loading: false }
    },
    deleteDist() {
      this.distDelete.loading = true

      this.$store
        .dispatch('distributors/delete', { id: this.distDelete.id })
        .then(response => {
          this.distDelete.loading = false
          this.distDelete.modal = false
          if (
            response &&
            (response.status === 200 || response.status === 204)
          ) {
            this.snack = {
              show: true,
              color: 'success',
              message: 'Distribuidor eliminado correctamente.',
            }
            this.asyncPagination()
          } else {
            this.snack = {
              show: true,
              color: 'error',
              message:
                (response && response.data && response.data.error) ||
                'No se pudo eliminar el distribuidor.',
            }
          }
        })
        .catch(() => {
          this.distDelete.loading = false
          this.distDelete.modal = false
          this.snack = {
            show: true,
            color: 'error',
            message: 'No se pudo conectar al servidor.',
          }
        })
    },
    async onDownload() {
      this.exporting = true
      this.snack = {
        show: true,
        color: '#254f87',
        message: 'Generando archivo Excel...',
      }
      try {
        const response = await this.$store.dispatch('distributors/getAll', {
          rpp: 9999,
          page: 1,
          ...this.pagination.search,
        })
        const rows = (response && response.items) || []
        if (!rows.length) {
          this.snack = {
            show: true,
            color: 'warning',
            message: 'No hay datos para exportar.',
          }
          this.exporting = false
          return
        }
        const headers = [
          'Distribuidor',
          'Teléfono',
          'Dirección',
          'Emails',
          'Contacto',
          'Estado',
        ]
        const thead = `<tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>`
        const tbody = rows
          .sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'es', {
              sensitivity: 'base',
            })
          )
          .map(dist => {
            const contact =
              this.hasNotifications(dist) && dist.contact
                ? `${dist.contact.firstName} ${dist.contact.lastName}`
                : ''
            const emails = Array.isArray(dist.emails)
              ? dist.emails.join(', ')
              : dist.emails || ''
            return `<tr>
              <td>${dist.name || ''}</td>
              <td>${dist.phone || ''}</td>
              <td>${dist.address || ''}</td>
              <td>${emails}</td>
              <td>${contact}</td>
              <td>${this.isActive(dist) ? 'Activo' : 'Inactivo'}</td>
            </tr>`
          })
          .join('')
        const html = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office"
                xmlns:x="urn:schemas-microsoft-com:office:excel"
                xmlns="http://www.w3.org/TR/REC-html40">
          <head><meta charset="UTF-8"></head>
          <body><table border="1">${thead}${tbody}</table></body>
          </html>`
        const blob = new Blob([html], {
          type: 'application/vnd.ms-excel;charset=utf-8;',
        })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `distribuidores_${new Date()
          .toISOString()
          .slice(0, 10)}.xls`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        this.snack = {
          show: true,
          color: 'success',
          message: `${rows.length} distribuidores exportados correctamente.`,
        }
      } catch (e) {
        this.snack = {
          show: true,
          color: 'error',
          message: 'Error al generar el archivo.',
        }
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<style scoped>
.dist-list-page {
  background: #f8fafc;
  min-height: 100vh;
  padding-bottom: 16px;
}

.list-hero {
  padding: 4px 0 8px;
}

.list-crumb {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.list-title {
  margin: 4px 0;
  font-size: 32px;
  line-height: 1.1;
  color: #0f172a;
  font-weight: 700;
}

.list-subtitle {
  margin: 0;
  font-size: 14px;
  color: #475569;
}

.dist-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  background: #ffffff;
}

.dist-card__toolbar {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.btn-download {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.1) !important;
  box-shadow: none !important;
}

.dist-card__content {
  padding: 0;
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
}

.dist-table {
  width: 100%;
  border-collapse: collapse;
}

.dist-table thead tr {
  background: #f8fafc;
}

.dist-table th,
.dist-table td {
  text-align: left;
  color: #334155;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  font-size: 14px;
}

.dist-table th {
  font-size: 12px;
  letter-spacing: 0.2px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
}

.dist-table tbody tr:hover {
  background: #f8fafc;
}

.th-actions {
  width: 90px;
}

.empty-cell {
  color: #94a3b8;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-edit {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.1) !important;
  box-shadow: none !important;
}

.btn-delete {
  border: 1px solid rgba(229, 37, 42, 0.3) !important;
  color: #e5252a !important;
  background: rgba(229, 37, 42, 0.1) !important;
  box-shadow: none !important;
}

/* Badge de estado */
.status-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.6;
}

.status-chip--active {
  color: #15803d;
  background: #dcfce7;
}

.status-chip--inactive {
  color: #64748b;
  background: #e2e8f0;
}

.empty-state {
  text-align: center !important;
  color: #64748b;
  padding: 18px !important;
}

.list-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.list-footer__count {
  font-size: 13px;
  color: #64748b;
}

.dist-card__content-mobile {
  padding: 12px;
}

.dist-mobile-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  margin-bottom: 10px;
}

.mobile-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.mobile-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  flex-shrink: 0;
}

.mobile-value {
  color: #334155;
  font-size: 13px;
  text-align: right;
}

.mobile-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.mobile-empty {
  text-align: center;
  padding: 16px;
  color: #64748b;
  font-size: 13px;
}

@media (max-width: 959px) {
  .list-title {
    font-size: 26px;
  }

  .dist-card__toolbar {
    padding: 10px;
  }

  .toolbar-actions {
    justify-content: flex-start;
    margin-top: 8px;
  }

  .list-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
